import { API_URLS } from '../constants/apiUrls';
import { signalRateLimit } from '../utils/rateLimitSignal';
import type { IdentityRequest, IdentityResponse, IdentityFailure } from '../types/api.types';

/**
 * POST /api/validate-identity
 * Raw HTTP call — no business logic. Returns response body or throws.
 * On failure, throws an Error whose message is the backend error code.
 * The error object also carries attemptsRemaining if provided.
 */
export async function postValidateIdentity(
  payload: IdentityRequest,
  jwtToken: string,
  formAccessId?: string | null
): Promise<IdentityResponse> {
  const response = await fetch(API_URLS.validateIdentity, {
    method:  'POST',
    headers: {
      'Content-Type':    'application/json',
      'Authorization':   `Bearer ${jwtToken}`,
      ...(formAccessId ? { 'X-Form-Access-Id': formAccessId } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    const errorCode = (body as { error?: string }).error ?? 'RATE_LIMIT_EXCEEDED';
    const retryAfter = parseInt(response.headers.get('Retry-After') ?? '600', 10);
    signalRateLimit(errorCode, retryAfter);
    throw new Error(errorCode);
  }

  const body = await response.json();

  if (!response.ok) {
    const err = body as IdentityFailure;
    const error = new Error(err.error ?? 'UNKNOWN_ERROR') as Error & {
      attemptsRemaining?: number;
      captchaId?:         string;
      imageBase64?:       string;
    };
    error.attemptsRemaining = err.attemptsRemaining;
    error.captchaId         = err.captchaId;
    error.imageBase64       = err.imageBase64;
    throw error;
  }

  return body as IdentityResponse;
}

/**
 * POST /api/token/validate
 * Validates a secure access token from the URL.
 */
export async function postValidateToken(token: string): Promise<{ valid: boolean; jwt: string }> {
  const response = await fetch(API_URLS.validateToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? 'TOKEN_ERROR');
  }

  return response.json();
}
